import client from "api/client";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { injectable } from "tsyringe";
import { DataFormat } from "utils/format/DataFormat";
import { StringFormat } from "utils/format/StringFormat";
import { IActionRepository } from "../domain/repositories/IActionRepository";

@injectable()
export class ActionRepositoryImpl implements IActionRepository {
  async getDetail<Params extends Object, Response extends Object>(
    path: string,
    params: Params,
  ): Promise<BaseApiResponseModel<Response>> {
    return await client?.get(path, params);
  }

  async create<Body extends Object, Response extends Object>(
    path: string,
    body: Body,
  ): Promise<BaseApiResponseModel<Response>> {
    return await client?.post(path, body);
  }

  async update<Body extends Object, Response extends Object>(
    path: string,
    body: Body,
  ): Promise<BaseApiResponseModel<Response>> {
    return await client?.put(path, body);
  }

  async delete<Params extends Object, Response extends Object>(
    path: string,
    params: Params,
  ): Promise<BaseApiResponseModel<Response>> {
    return await client?.delete(path, params);
  }

  async export<Params extends Object>(
    path: string,
    params: Params,
  ): Promise<BaseApiResponseModel<Blob>> {
    const url = StringFormat.buildExportUrl(path, params);
    const token = DataFormat.getToken();

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "x-export": "excel",
        "Content-Type": "application/xml",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorBody = await response.json();
      return errorBody;
    }

    const blob = await response.blob();

    return {
      success: true,
      data: blob,
      message: "Export successful",
    } as unknown as BaseApiResponseModel<Blob>;
  }

  async combineWithFormData<Response extends Object>(
    path: string,
    formData: FormData,
  ): Promise<BaseApiResponseModel<Response>> {
    return await client?.post(path, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }
}
